import React, { useState, useMemo } from 'react';
import get from 'lodash/get';
import Button from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import Lozenge from '@atlaskit/lozenge';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import { N500 } from '@atlaskit/theme/colors';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { DynamicResultTable, FormattedDate, useFeatureToggle, FormatCurrency } from '@css-xp/base';
import { BlendedPartnerDiscount } from '@css-xp/ppc-blended-partner-discount';
import { ENABLE_DESIGN_ITERATION_1 } from '@css-xp/ppc-constants';
import { emptyStateProp } from '@css-xp/ppc-common/src/empty-state-prop';
import { PartnerMarginModalStyles, ViewDetailsButton } from './partner-margin-modal.styles';

interface PartnerMarginModalProps {
  margin?: any[];
  startDate: string;
  endDate: string;
  currency: string | null;
}

export const PartnerMarginModal = ({ margin, startDate, endDate, currency }: PartnerMarginModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);

  const columns = {
    cells: [
      {
        key: 'Period',
        content: 'Period',
        width: 30,
      },
      {
        key: 'Discount type',
        content: 'Discount type',
        width: 30,
      },
      {
        key: 'Percentage',
        content: 'Percentage',
        width: 20,
      },
      {
        key: `Partner discount (${currency})`,
        content: `Partner discount (${currency})`,
        width: 20,
      },
    ],
  };

  const rows = useMemo(() => {
    return [
      {
        key: 'partner-margin',
        cells: [
          {
            content: (
              <>
                <FormattedDate date={new Date(startDate).toISOString()} format={NEW_DATE_FORMAT} />
                <span> - </span>
                <FormattedDate date={new Date(endDate).toISOString()} format={NEW_DATE_FORMAT} />
              </>
            ),
          },
          {
            content: `${get(margin, 'blended', '') ? 'Blended ' : ''}Partner discount`,
          },
          {
            content: <Lozenge>{get(margin, 'percent', '')}%</Lozenge>,
          },
          {
            content: (
              <FormatCurrency
                showCommas
                value={(get(margin, 'amount', 0) ? get(margin, 'amount', 0) : 0).toFixed(2)}
                currency={currency}
              />
            ),
          },
        ],
      },
    ];
  }, [startDate, endDate, margin]);

  const closeModal = () => setIsOpen(false);

  return (
    <>
      {isOpen && (
        <ModalTransition>
          <Modal onClose={closeModal} width="x-large" testId="partner-margin-modal" autoFocus={false}>
            <ModalHeader>
              <ModalTitle>Discount details</ModalTitle>
              <Button appearance="link" onClick={closeModal} testId="partner-margin-modal-close-button-with-icon">
                <CrossIcon size="small" label="Close Modal" primaryColor={token('color.text.subtle', N500)} />
              </Button>
            </ModalHeader>
            <ModalBody>
              <PartnerMarginModalStyles>
                <DynamicResultTable head={columns} rows={rows} boxShadow={false} emptyStateProps={emptyStateProp} />
              </PartnerMarginModalStyles>
              {get(margin, 'blended', false) && (
                <BlendedPartnerDiscount
                  blendedMargin={margin as any}
                  currency={currency || ''}
                  shouldConvertValueBasedOnCurrency={false}
                />
              )}
            </ModalBody>
            <ModalFooter>
              <Button onClick={closeModal} testId="partner-margin-modal-close-button">
                Close
              </Button>
            </ModalFooter>
          </Modal>
        </ModalTransition>
      )}
      <ViewDetailsButton isDesignIteration1Enabled={isDesignIteration1Enabled}>
        <Button appearance="link" onClick={() => setIsOpen(true)} testId="view-partner-margin-details-button">
          View details
        </Button>
      </ViewDetailsButton>
    </>
  );
};
